// insights-derive.ts — Deterministic "proactive insights" engine.
//
// Pure function, no side effects, no AI dependency — offline/no-key safe.
// Rules fire against thresholds vs historical averages (simple, KISS).
//
// Deliberately does NOT re-emit:
//   - "n việc quá hạn" (already in AttentionDigest / today-digest-derive)
//   - "ngân sách sắp chạm" (already in AttentionDigest budgetAlertCount chip)
//
// Cap: max 5 insights total (sorted warn-first then info).
// Stable IDs: `overspend:{category}`, `savings-rate`, `overdue-project:{id}`,
//             `habit-slipping:{id}`.

import { budgetStatuses, monthKeyOf, monthLedger } from "./bento-derive";
import { habitStreakCurrent, habitStreakLongest } from "./habit-derive";
import { todayIso, formatMoney } from "../lib/date";
import type { LifeOsState } from "../types";
import type { LifeOsStateWithRecurring } from "../lib/life-os-contract";

// ─── public types ─────────────────────────────────────────────────────────────

export type InsightSeverity = "warn" | "info";

export type Insight = {
  id: string;
  severity: InsightSeverity;
  icon: string;
  title: string;
  detail: string;
  /** Which view to navigate to when clicked. */
  view?: string;
};

// ─── internal helpers ─────────────────────────────────────────────────────────

/** Build YYYY-MM key N months before the given monthKey. */
function monthKeyOffset(mk: string, offsetMonths: number): string {
  const year = Number(mk.slice(0, 4));
  const month = Number(mk.slice(5, 7));
  const raw = month + offsetMonths; // offsetMonths is negative for past months
  const y = year + Math.floor((raw - 1) / 12);
  const m = ((raw - 1 + 120) % 12) + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}

/** A minimal ledger row shape used by the internal helpers below. */
type LedgerRow = { type: string; category: string; amount: number; toAccountId?: string };

/** Sum expense amounts for a specific category, transfers excluded. */
function categoryExpense(ledger: LedgerRow[], category: string): number {
  return ledger
    .filter((e) => e.type === "expense" && !e.toAccountId && e.category === category)
    .reduce((s, e) => s + e.amount, 0);
}

/** Collect all expense category names present in a ledger, transfers excluded. */
function expenseCategories(ledger: LedgerRow[]): string[] {
  const seen = new Set<string>();
  for (const e of ledger) {
    if (e.type === "expense" && !e.toAccountId) seen.add(e.category);
  }
  return [...seen];
}

/** Sum all income in ledger, transfers excluded. */
function ledgerIncome(ledger: LedgerRow[]): number {
  return ledger
    .filter((e) => e.type === "income" && !e.toAccountId)
    .reduce((s, e) => s + e.amount, 0);
}

/** Sum all expense in ledger, transfers excluded. */
function ledgerExpense(ledger: LedgerRow[]): number {
  return ledger
    .filter((e) => e.type === "expense" && !e.toAccountId)
    .reduce((s, e) => s + e.amount, 0);
}

// ─── Rule 1: category overspend ───────────────────────────────────────────────

const OVERSPEND_MIN_VND = 200_000;
const OVERSPEND_WARN_MULTIPLIER = 1.5;
const OVERSPEND_INFO_MULTIPLIER = 1.3;

function categoryOverspendInsights(
  state: LifeOsStateWithRecurring,
  thisMonthKey: string,
  today: string,
): Insight[] {
  const thisLedger = monthLedger(state, thisMonthKey, today);
  const results: Insight[] = [];

  // Gather categories from current month
  const categories = expenseCategories(thisLedger);

  for (const cat of categories) {
    const spentThisMonth = categoryExpense(thisLedger, cat);
    if (spentThisMonth < OVERSPEND_MIN_VND) continue;

    // Compute average of 3 prior full months
    let priorTotal = 0;
    let priorMonthCount = 0;
    for (let i = 1; i <= 3; i++) {
      const mk = monthKeyOffset(thisMonthKey, -i);
      const ledger = monthLedger(state, mk, today);
      const spent = categoryExpense(ledger, cat);
      // Only include months that had any data for this category
      if (spent > 0) {
        priorTotal += spent;
        priorMonthCount++;
      }
    }

    if (priorMonthCount === 0) continue; // no prior data — skip
    const avgPrior = priorTotal / priorMonthCount;
    if (avgPrior <= 0) continue;

    const ratio = spentThisMonth / avgPrior;
    if (ratio < OVERSPEND_INFO_MULTIPLIER) continue;

    const pct = Math.round((ratio - 1) * 100);
    const severity: InsightSeverity = ratio >= OVERSPEND_WARN_MULTIPLIER ? "warn" : "info";

    results.push({
      id: `overspend:${cat}`,
      severity,
      icon: severity === "warn" ? "⚠️" : "📊",
      title: `Chi ${cat} cao hơn thường`,
      detail: `Chi ${cat} tháng này ${formatMoney(spentThisMonth)} — cao hơn ${pct}% so với trung bình 3 tháng.`,
      view: "finance",
    });
  }

  return results;
}

// ─── Rule 2: savings-rate drop ────────────────────────────────────────────────

const SAVINGS_RATE_DROP_THRESHOLD = 10; // percentage points

function savingsRateDropInsight(
  state: LifeOsStateWithRecurring,
  thisMonthKey: string,
  today: string,
): Insight | null {
  // Current month savings rate
  const thisLedger = monthLedger(state, thisMonthKey, today);
  const thisIncome = ledgerIncome(thisLedger);
  if (thisIncome <= 0) return null; // can't compute — skip

  const thisExpense = ledgerExpense(thisLedger);
  const thisRate = ((thisIncome - thisExpense) / thisIncome) * 100;

  // Average savings rate of prior 3 full months (positive income only)
  let priorRateSum = 0;
  let priorCount = 0;
  for (let i = 1; i <= 3; i++) {
    const mk = monthKeyOffset(thisMonthKey, -i);
    const ledger = monthLedger(state, mk, today);
    const inc = ledgerIncome(ledger);
    if (inc <= 0) continue;
    const exp = ledgerExpense(ledger);
    priorRateSum += ((inc - exp) / inc) * 100;
    priorCount++;
  }

  if (priorCount === 0) return null; // no prior data
  const avgPriorRate = priorRateSum / priorCount;
  if (avgPriorRate <= 0) return null; // prior was already non-positive

  const drop = avgPriorRate - thisRate;
  if (drop < SAVINGS_RATE_DROP_THRESHOLD) return null;

  return {
    id: "savings-rate",
    severity: "info",
    icon: "📉",
    title: "Tỷ lệ tiết kiệm giảm",
    detail: `Tỷ lệ tiết kiệm tháng này ${Math.round(thisRate)}% (thường ~${Math.round(avgPriorRate)}%).`,
    view: "finance",
  };
}

// ─── Rule 3: overdue cluster by project ───────────────────────────────────────

const OVERDUE_CLUSTER_MIN = 3;
const OVERDUE_PROJECT_MAX_INSIGHTS = 2;

function overdueProjectInsights(
  state: Pick<LifeOsState, "tasks" | "projects">,
  today: string,
): Insight[] {
  const openOverdue = state.tasks.filter(
    (t) => t.status !== "done" && t.dueDate && t.dueDate < today && t.projectId,
  );

  // Group by projectId
  const byProject = new Map<string, number>();
  for (const task of openOverdue) {
    if (!task.projectId) continue;
    byProject.set(task.projectId, (byProject.get(task.projectId) ?? 0) + 1);
  }

  const results: Insight[] = [];
  for (const [projectId, count] of byProject) {
    if (count < OVERDUE_CLUSTER_MIN) continue;
    const project = state.projects.find((p) => p.id === projectId);
    const name = project?.name ?? projectId;
    results.push({
      id: `overdue-project:${projectId}`,
      severity: "warn",
      icon: "🚧",
      title: `Dự án "${name}" đang nghẽn`,
      detail: `Dự án "${name}": ${count} việc quá hạn — đang nghẽn.`,
      view: "tasks",
    });
    if (results.length >= OVERDUE_PROJECT_MAX_INSIGHTS) break;
  }

  return results;
}

// ─── Rule 4: habit slipping ───────────────────────────────────────────────────

const HABIT_ESTABLISHED_MIN_STREAK = 5;
const HABIT_SLIP_DENOMINATOR = 3; // current ≤ floor(longest / 3)
const HABIT_SLIP_MAX_INSIGHTS = 2;

function habitSlippingInsights(
  state: Pick<LifeOsState, "habits">,
  today: string,
): Insight[] {
  const results: Insight[] = [];

  for (const habit of state.habits) {
    const longest = habitStreakLongest(habit.completions, today);
    if (longest < HABIT_ESTABLISHED_MIN_STREAK) continue;

    const current = habitStreakCurrent(habit.completions, today);
    if (current > Math.floor(longest / HABIT_SLIP_DENOMINATOR)) continue;

    results.push({
      id: `habit-slipping:${habit.id}`,
      severity: "info",
      icon: "🔥",
      title: `Thói quen "${habit.name}" đang tụt`,
      detail: `Thói quen "${habit.name}" đang tụt (chuỗi ${current}, từng đạt ${longest}).`,
      view: "habits",
    });

    if (results.length >= HABIT_SLIP_MAX_INSIGHTS) break;
  }

  return results;
}

// ─── Rule 5: predictive budget burn-rate (P4-4) ───────────────────────────────
// Early warning BEFORE the threshold chip fires: if the current daily spend pace
// projects the envelope to run out before month-end, surface the exhaust day.
// Gated below alertThreshold so it doesn't duplicate the "sắp chạm" digest chip.

const BURN_MIN_DAY = 3;            // need a few days of data to project meaningfully
const BURN_MAX_INSIGHTS = 2;

function budgetBurnRateInsights(
  state: LifeOsStateWithRecurring,
  today: string,
): Insight[] {
  const dayOfMonth = Number(today.slice(8, 10));
  if (dayOfMonth < BURN_MIN_DAY) return [];
  const [y, m] = today.slice(0, 7).split("-").map(Number);
  const daysInMonth = new Date(y, m, 0).getDate();
  if (dayOfMonth >= daysInMonth) return []; // last day → projection moot

  const results: Insight[] = [];
  for (const s of budgetStatuses(state, monthKeyOf(today), today)) {
    if (s.spent <= 0 || s.over || s.effectiveLimit <= 0) continue;
    // Skip if already at the threshold chip's trigger — avoid duplicate signal.
    const pct = (s.spent / s.effectiveLimit) * 100;
    if (pct >= s.alertThreshold) continue;

    const dailyRate = s.spent / dayOfMonth;
    const projected = dailyRate * daysInMonth;
    if (projected <= s.effectiveLimit) continue; // on track for the month

    const exhaustDay = Math.ceil(s.effectiveLimit / dailyRate);
    if (exhaustDay > daysInMonth) continue; // won't actually run out this month

    results.push({
      id: `budget-burn:${s.budget.id}`,
      severity: "warn",
      icon: "📈",
      title: `Ngân sách ${s.budget.category} sắp cạn sớm`,
      detail: `Với đà chi hiện tại, ngân sách ${s.budget.category} dự kiến cạn vào ngày ${exhaustDay} (đã chi ${formatMoney(s.spent)}/${formatMoney(s.effectiveLimit)}).`,
      view: "finance",
    });
    if (results.length >= BURN_MAX_INSIGHTS) break;
  }
  return results;
}

// ─── main export ──────────────────────────────────────────────────────────────

const INSIGHT_CAP = 5;

/**
 * buildInsights — deterministic proactive insights engine.
 *
 * Returns up to INSIGHT_CAP insights: warn-first, then info.
 * Skips any rule when data is insufficient (avoids false positives).
 * Does NOT duplicate Today's AttentionDigest warnings (overdue count, budget alerts).
 */
export function buildInsights(state: LifeOsStateWithRecurring, today = todayIso()): Insight[] {
  const thisMonthKey = monthKeyOf(today);

  // Collect from all rules (call each rule exactly once)
  const savingsInsight = savingsRateDropInsight(state, thisMonthKey, today);
  const raw: Insight[] = [
    ...categoryOverspendInsights(state, thisMonthKey, today),
    ...(savingsInsight ? [savingsInsight] : []),
    ...overdueProjectInsights(state, today),
    ...habitSlippingInsights(state, today),
    ...budgetBurnRateInsights(state, today),
  ];

  // Sort: warn-first, then info; stable within each group (insertion order)
  const warns = raw.filter((i) => i.severity === "warn");
  const infos = raw.filter((i) => i.severity === "info");

  return [...warns, ...infos].slice(0, INSIGHT_CAP);
}
